import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ClipboardList } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { toList } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, Btn, shortDate } from "../../components/app/kit";
import { AddGame } from "./Games";
import { scoreOf } from "./Overview";

/** One place to start stat entry: pick a game (newest first) or log a new one. ?player= highlights that player's row. */
export default function EnterStats() {
  const { membership } = useAuth();
  const tid = membership?.team_id;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const player = params.get("player");
  const suffix = player ? `?player=${player}` : "";
  const { data, loading, error } = useApi(tid && `/teams/${tid}/games`);
  const [adding, setAdding] = useState(false);
  const games = toList(data, ["games"]).sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? "")));
  return (
    <div>
      <PageHeader eyebrow={membership?.team_name} title="Enter stats">
        <Btn data-testid="enter-stats-new-game" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />New game</Btn>
      </PageHeader>
      <p className="-mt-4 mb-6 text-sm text-slate-300">Pick a game to type in each player's box score, or log a new game first. Stats sync to the GAMEDAY app.</p>
      <AddGame open={adding} onClose={() => setAdding(false)} tid={tid} suffix={suffix} />
      {loading && !data ? <Loader /> : error ? <ErrorNote error={error} /> : !games.length ? (
        <Empty title="No games yet" text="Tap New game to log your first game and enter its stats." />
      ) : (
        <ul className="space-y-3">
          {games.map((g) => {
            const { us, them } = scoreOf(g);
            const has = g.lines && Object.keys(g.lines).length > 0;
            return (
              <li key={g.id} data-testid={`enter-stats-game-${g.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#1E293B] bg-[#0F172A] px-5 py-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-xl font-bold uppercase text-white">vs {g.opponent ?? "Opponent"}</p>
                  <p className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">{shortDate(g.date)} · {us ?? "–"}:{them ?? "–"} · {has ? "Stats entered" : "No stats yet"}</p>
                </div>
                <Btn variant={has ? "ghost" : "lime"} data-testid={`enter-stats-open-${g.id}`} onClick={() => navigate(`/coach/games/${g.id}/stats${suffix}`)} className="px-4 py-2 text-xs">
                  <ClipboardList className="h-3.5 w-3.5" />{has ? "Edit stats" : "Enter stats"}
                </Btn>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
